import { z } from "zod";

import type { EmbeddedStudioDefaultProps } from "@mountayaapp/types/api/api";

import { hexColorSchema } from "./segmentColors";

/**
 * languageSchema mirrors the backend Language enum (the embed's preferred_language
 * override). Tightened to the generated values so an out-of-range URL param is
 * dropped rather than carried as a free string.
 */
export const languageSchema = z.enum(["en", "fr", "es", "it", "de", "zh", "ja"]);

/**
 * themeSchema mirrors the backend Theme enum. It is the org's stored
 * default_props.preferred_theme vocabulary (auto/light/dark) — the in-app studio
 * concept where "auto" follows the system preference. The embed narrows this to
 * embedThemeSchema below.
 */
export const themeSchema = z.enum(["auto", "light", "dark"]);

/**
 * embedThemeSchema is the embed-only theme vocabulary: light or dark, never "auto".
 * An embed renders inside the publisher's page and must match that page's scheme — a
 * deliberate light/dark choice — rather than follow the visitor's OS preference (what
 * "auto" tracks), which the publisher cannot preview and which can clash with their
 * page. resolveEmbedConfig clamps an org default of "auto" to light, so an embed never
 * resolves "auto".
 */
export const embedThemeSchema = z.enum(["light", "dark"]);

/**
 * EmbedTheme is the resolved embed theme (light | dark), derived from embedThemeSchema.
 */
export type EmbedTheme = z.infer<typeof embedThemeSchema>;

/**
 * unitLengthSchema mirrors the backend UnitLength enum (the embed's
 * preferred_unit_length override).
 */
export const unitLengthSchema = z.enum(["metric", "imperial"]);

/**
 * unitSlopeSchema mirrors the backend UnitSlope enum (the embed's
 * preferred_unit_slope override).
 */
export const unitSlopeSchema = z.enum(["degrees", "percentage"]);

/**
 * unitTemperatureSchema mirrors the backend UnitTemperature enum (the embed's
 * preferred_unit_temperature override).
 */
export const unitTemperatureSchema = z.enum(["celsius", "fahrenheit"]);

/**
 * terrainVariantSchema mirrors the backend TerrainVariant enum (the embed's base
 * map light/dark tint). Distinct from the terrain SLUG, which is a free string.
 */
export const terrainVariantSchema = z.enum(["light", "dark"]);

/**
 * TerrainVariant is the studio base-layer light/dark tint, derived from
 * terrainVariantSchema. Distinct from the terrain slug, which is a free string.
 */
export type TerrainVariant = z.infer<typeof terrainVariantSchema>;

/**
 * segmentsColorSchema is the embed's single-segment-color vocabulary: a hex color,
 * or the empty string meaning "no tint, every segment keeps its own color". The empty
 * member is not slack — it is the value that turns the tint OFF, both as the org's
 * stored default and as an explicit per-embed URL override, so it has to survive the
 * parse rather than be caught away. It reuses hexColorSchema, the same rule the API
 * persists segment colors under, so a tint and a segment color can never diverge on
 * what a valid color is.
 */
export const segmentsColorSchema = z.union([hexColorSchema, z.literal("")]);

/**
 * DEFAULT_TERRAIN is the fallback terrain slug when neither the URL, the org
 * default_props, nor the UI store specify one. It seeds the studio UI store and
 * backstops resolveEmbedConfig's terrain precedence.
 */
export const DEFAULT_TERRAIN = "topo";

/**
 * optionalString accepts a present, non-empty search-param string and otherwise
 * resolves to undefined — so an empty `?activity=` reads identically to an
 * absent param. Shared by the map-state content params.
 */
const optionalString = z.string().min(1).optional().catch(undefined);

/**
 * optionalStringArray accepts the multi-value form (`?with_overlays=a&with_overlays=b`)
 * for the embed's overlay/terrain allowlists. A single value still parses to a
 * one-element array; an absent param resolves to undefined (so the org default
 * wins in resolveEmbedConfig).
 */
const optionalStringArray = z
  .union([z.string(), z.array(z.string())])
  .transform((value) => (Array.isArray(value) ? value : [value]))
  .optional()
  .catch(undefined);

/**
 * urlBoolean parses a URL boolean into a tri-state: true, false, or undefined
 * (param absent / unrecognized). undefined is the signal that the default should
 * win — only an explicit true/false overrides it. The input is a union of the
 * textual `"true"`/`"false"` form a fresh URL carries (case-insensitive) and a
 * real boolean, so the value round-trips: the authenticated studio writes the
 * parsed boolean straight back through the search updater without re-stringifying.
 */
export const urlBoolean = z
  .union([
    z.boolean(),
    z
      .string()
      .transform((value) => value.toLowerCase())
      .pipe(z.enum(["true", "false"]))
      .transform((value) => value === "true"),
  ])
  .optional()
  .catch(undefined);

/**
 * TIME_OFFSET_MIN_MINUTES and TIME_OFFSET_MAX_MINUTES bound the overlay
 * time_offset to the time-slider's window — 71 hours in the past to 144 hours
 * (6 days) in the future, expressed in minutes. The forecast overlays only carry
 * tiles inside this window, so an out-of-range offset is clamped to the nearest
 * edge rather than dropped.
 */
const TIME_OFFSET_MIN_MINUTES = -4260;
const TIME_OFFSET_MAX_MINUTES = 8640;

/**
 * timeOffset parses the overlay time as an integer count of minutes from the
 * viewer's current clock (negative = the past), clamped to the slider window
 * [TIME_OFFSET_MIN_MINUTES, TIME_OFFSET_MAX_MINUTES]. An absent or unparseable
 * value resolves to undefined, which the studio reads as "now".
 *
 * The URL encodes the time RELATIVELY (minutes-from-now) on purpose: a shared
 * link to "+6 days" must mean +6 days from whoever opens it, not a frozen instant
 * that drifts out of the forecast window as time passes. The store still holds the
 * absolute ISO instant it renders (overlayTimestamp); only the URL is relative,
 * and the studio converts between the two at the URL boundary.
 */
const timeOffset = z
  .union([z.number(), z.string().transform((value) => Number(value))])
  .pipe(z.number().int())
  .optional()
  .catch(undefined)
  .transform((value) =>
    value === undefined
      ? undefined
      : Math.min(Math.max(value, TIME_OFFSET_MIN_MINUTES), TIME_OFFSET_MAX_MINUTES),
  );

/**
 * CAMERA_ZOOM_MIN and CAMERA_ZOOM_MAX bound the publisher's default-view zoom to a
 * sane web-map range, so a malformed or extreme `?zoom=` clamps to the nearest edge
 * rather than slamming the camera to an unusable level. The org's own safety zoom
 * range narrows this further at apply time in the embedded studio; this is only the
 * URL-parse guard.
 */
const CAMERA_ZOOM_MIN = 0;
const CAMERA_ZOOM_MAX = 24;

/**
 * CAMERA_BEARING_MAX and CAMERA_PITCH_MAX bound the default-view orientation: the
 * bearing wraps into [0, 360) and the pitch clamps to [0, 85] (MapLibre's maximum
 * tilt), matching the renderer's own limits so a captured or hand-set angle is
 * always one the map can adopt.
 */
const CAMERA_BEARING_MAX = 360;
const CAMERA_PITCH_MAX = 85;

/**
 * boundedNumber builds a parser for an optional numeric search param clamped into
 * [min, max]. It mirrors the timeOffset shape (a number-or-numeric-string union, an
 * absent/unparseable value resolving to undefined) so the three default-view camera
 * params — zoom, bearing, pitch — share one well-tested clamp rather than restating
 * it. The transform runs after the union/pipe so only a real number is clamped.
 */
const boundedNumber = (min: number, max: number) =>
  z
    .union([z.number(), z.string().transform((value) => Number(value))])
    .pipe(z.number())
    .optional()
    .catch(undefined)
    .transform((value) => (value === undefined ? undefined : Math.min(Math.max(value, min), max)));

/**
 * cameraZoom parses the publisher's default-view zoom, clamped to
 * [CAMERA_ZOOM_MIN, CAMERA_ZOOM_MAX]. Absent → undefined (the embed keeps the plain
 * fit-to-route, with no zoom override).
 */
const cameraZoom = boundedNumber(CAMERA_ZOOM_MIN, CAMERA_ZOOM_MAX);

/**
 * cameraBearing parses the publisher's default-view bearing in degrees, wrapped into
 * [0, CAMERA_BEARING_MAX). A captured 360 reads as 0 (north); an absent param leaves
 * the embed north-up. The wrap (modulo) keeps a value the renderer accepts and a
 * full turn collapses to north rather than clamping to 359.
 */
const cameraBearing = z
  .union([z.number(), z.string().transform((value) => Number(value))])
  .pipe(z.number())
  .optional()
  .catch(undefined)
  .transform((value) =>
    value === undefined
      ? undefined
      : ((value % CAMERA_BEARING_MAX) + CAMERA_BEARING_MAX) % CAMERA_BEARING_MAX,
  );

/**
 * cameraPitch parses the publisher's default-view pitch in degrees, clamped to
 * [0, CAMERA_PITCH_MAX] (MapLibre's tilt ceiling). Absent → undefined (the embed
 * opens flat).
 */
const cameraPitch = boundedNumber(0, CAMERA_PITCH_MAX);

/**
 * CAMERA_CENTER_LNG_MAX and CAMERA_CENTER_LAT_MAX bound the default-view center: the
 * longitude to the valid WGS84 range [-180, 180] and the latitude to the Web Mercator
 * limit [-85, 85] (beyond it the projection — and the renderer's center — is
 * undefined). A captured center always lands inside these, so a coordinate that
 * exceeds either axis is a malformed param and is dropped.
 */
const CAMERA_CENTER_LNG_MAX = 180;
const CAMERA_CENTER_LAT_MAX = 85;

/**
 * cameraCenterTuple is the validated [lng, lat] both input forms normalize to: a
 * finite longitude in [-180, 180] and latitude in [-85, 85] (the WGS84 + Web Mercator
 * limits). A value outside either axis fails the pipe, so cameraCenter's `.catch`
 * drops it.
 */
const cameraCenterTuple = z.tuple([
  z.number().finite().min(-CAMERA_CENTER_LNG_MAX).max(CAMERA_CENTER_LNG_MAX),
  z.number().finite().min(-CAMERA_CENTER_LAT_MAX).max(CAMERA_CENTER_LAT_MAX),
]);

/**
 * cameraCenter parses the publisher's default-view center into a [lng, lat] tuple. The
 * center is the tile coordinate the publisher framed, so the embed opens on exactly
 * that anchor rather than re-centering on the fit-to-route. It accepts a union of the
 * textual `"lng,lat"` form a fresh URL carries (split on the comma into two finite
 * numbers) and the already-parsed tuple, so the value ROUND-TRIPS: the authenticated
 * studio writes the parsed tuple straight back through the search updater without
 * re-stringifying — the same union shape the numeric camera params and urlBoolean use.
 * Either form is range-validated (lng ∈ [-180, 180], lat ∈ [-85, 85]); a missing comma,
 * a non-numeric half, or an out-of-range value resolves to undefined (`.catch`), so a
 * malformed param is dropped and the embed falls back to the plain fit. It is paired
 * with the zoom/bearing/pitch params: together they are the full camera anchor.
 */
const cameraCenter = z
  .union([
    z
      .string()
      .transform((value) => value.split(","))
      .pipe(z.tuple([z.string(), z.string()]))
      .transform(([lng, lat]) => [Number(lng), Number(lat)] as [number, number]),
    cameraCenterTuple,
  ])
  .pipe(cameraCenterTuple)
  .optional()
  .catch(undefined);

/**
 * mapStateShape is the raw object shape of the shared map-state facet. It is
 * shared (not merged) so the standalone studioMapStateSearchSchema, the merged
 * embedSearchSchema, and the authenticated studio route all build from one
 * source rather than restating the content/display params. The route extends it
 * with the agent-thread params.
 */
export const mapStateShape = {
  activity: optionalString,
  bearing: cameraBearing,
  center: cameraCenter,
  collection_id: optionalString,
  enrichments: optionalStringArray,
  itinerary_id: optionalString,
  overlay: optionalString,
  pitch: cameraPitch,
  terrain: optionalString,
  time_offset: timeOffset,
  zone_id: optionalString,
  zoom: cameraZoom,
} as const;

/**
 * MAP_STATE_SEARCH_KEYS names the search params that mirror the VISIBLE map
 * state — the overlay, its stacked enrichments, the base terrain, and the
 * overlay time offset — which the authenticated studio live-writes back into the
 * URL as the user toggles them. It is the single source of truth shared by the
 * URL-sync hook (which mirrors exactly these keys) and the unsaved-edits blocker
 * (which exempts a same-route navigation that changes only these keys, so
 * toggling an overlay mid-edit does not raise the leave-an-edit prompt). The
 * content-selection keys (itinerary_id and the rest of mapStateShape)
 * are deliberately absent: changing those is a real content navigation the
 * blocker must still gate.
 */
export const MAP_STATE_SEARCH_KEYS = ["overlay", "enrichments", "terrain", "time_offset"] as const;

/**
 * MapStateSearchKey is one live-written map-state search param, derived from
 * MAP_STATE_SEARCH_KEYS.
 */
export type MapStateSearchKey = (typeof MAP_STATE_SEARCH_KEYS)[number];

/**
 * AGENT_PANEL_SEARCH_KEYS names the co-pilot panel's URL keys — the ordered
 * thread set (`threads`) and the active tab (`activeThread`) — which
 * AgentThreadsProvider mirrors into the route search as the user opens, closes,
 * or switches an AI conversation, so a reload reconnects every in-flight chat.
 * Like the map-state keys, toggling the co-pilot REFINES the current view rather
 * than leaving it: opening the assistant enhances the edit in progress, it does
 * not navigate away from it. The unsaved-edits blocker unions these with
 * MAP_STATE_SEARCH_KEYS so a same-route navigation whose only changed keys are
 * the panel keys never raises the leave-an-edit prompt.
 */
export const AGENT_PANEL_SEARCH_KEYS = ["activeThread", "threads"] as const;

/**
 * studioMapStateSearchSchema is the SHARED studio URL-state facet: the
 * content/display params the authenticated webapp live-writes and the embed reads
 * as static config. itinerary_id is the single content selector.
 */
export const studioMapStateSearchSchema = z.object(mapStateShape);

/**
 * StudioMapStateSearch is the parsed shared map-state facet, derived from
 * studioMapStateSearchSchema.
 */
export type StudioMapStateSearch = z.infer<typeof studioMapStateSearchSchema>;

/**
 * embedConfigSearchSchema is the embed-only facet: the preference overrides, the
 * terrain variant, the boolean with_* control flags (textual "true"/"false"),
 * and the with_overlays/with_terrains allowlist arrays. Every field is optional
 * here; resolveEmbedConfig applies the URL > default_props > fallback precedence.
 * `embedded` is always true, so it does not appear here.
 */
export const embedConfigSearchSchema = z.object({
  preferred_language: languageSchema.optional().catch(undefined),
  preferred_theme: embedThemeSchema.optional().catch(undefined),
  preferred_unit_length: unitLengthSchema.optional().catch(undefined),
  preferred_unit_slope: unitSlopeSchema.optional().catch(undefined),
  preferred_unit_temperature: unitTemperatureSchema.optional().catch(undefined),
  // An EMPTY value is meaningful and distinct from an absent one: `segments_color=`
  // turns the tint off for this embed even when the org sets one, while omitting the
  // param falls through to the org default. Only a malformed value catches to
  // undefined.
  segments_color: segmentsColorSchema.optional().catch(undefined),
  terrain_variant: terrainVariantSchema.optional().catch(undefined),
  with_directions_control: urlBoolean,
  with_download_control: urlBoolean,
  with_navigation_control: urlBoolean,
  with_overlays: optionalStringArray,
  with_overlays_control: urlBoolean,
  with_preferences_control: urlBoolean,
  with_profile_analysis: urlBoolean,
  with_profile_characteristics: urlBoolean,
  with_profile_elevation: urlBoolean,
  with_streetview_control: urlBoolean,
  with_terrains: optionalStringArray,
});

/**
 * EmbedConfigSearch is the parsed embed-only facet, derived from
 * embedConfigSearchSchema.
 */
export type EmbedConfigSearch = z.infer<typeof embedConfigSearchSchema>;

/**
 * embedAuthSearchSchema is the credential facet: the publishable key that
 * resolves the org and the optional session token for require_session_token
 * orgs. These are passed to the API embed-auth strategy (X-API-Key /
 * X-Session-Token), never named with the reserved credential query params.
 *
 * `preview` is the owner-truthful preview signal the embed wizard sets on its OWN
 * preview iframe (never the copyable snippet). When true, the same-origin preview
 * authenticates the surface's data + tile reads with the owner's session cookie
 * instead of the publishable key — so the publisher sees their own itinerary
 * regardless of privacy — while a real cross-site embed (which never carries this
 * flag, and whose cookie would not ride cross-site anyway) stays visitor-gated. It
 * is a same-origin-only convenience: the host CSP route still gates the document.
 *
 * `organization_id` is the PREVIEW-ONLY transport that lets the preview resolve the
 * org by id over the publisher's session (the publishable-key lookup the visitor
 * path uses is unavailable when no key is selected). It rides only on the wizard's
 * own preview iframe alongside `preview`; it is never part of the resolved
 * EmbedConfig and never emitted into the copyable snippet, so a visitor's URL never
 * carries it. A visitor (no `preview`) still resolves the org from the publishable
 * key and never reads this param.
 */
export const embedAuthSearchSchema = z.object({
  organization_id: optionalString,
  preview: urlBoolean,
  publishable_key: optionalString,
  session_token: optionalString,
});

/**
 * EmbedAuthSearch is the parsed credential facet, derived from
 * embedAuthSearchSchema.
 */
export type EmbedAuthSearch = z.infer<typeof embedAuthSearchSchema>;

/**
 * embedCompatShape carries route_id, a temporary compatibility alias for
 * itinerary_id. Some embeds still pass the itinerary as `route_id`; the embed
 * schema accepts it and resolveEmbedConfig folds it into itineraryId (itinerary_id
 * wins when both are set). It is scoped to the embed schema so the authenticated
 * /studio route does not inherit it, and is slated for removal — do not build on it.
 */
const embedCompatShape = { route_id: optionalString } as const;

/**
 * embedSearchSchema is the merged /studio/embed search schema: the three facets
 * the embed route's validateSearch composes. It extends the auth + config object
 * schemas with the shared map-state shape, so the embed reads the content/display
 * params alongside the credentials and preferences in one parse. It also carries the
 * temporary route_id compatibility alias (embedCompatShape).
 */
export const embedSearchSchema = embedAuthSearchSchema
  .extend(embedConfigSearchSchema.shape)
  .extend(mapStateShape)
  .extend(embedCompatShape);

/**
 * EmbedSearch is the parsed merged embed search, derived from embedSearchSchema.
 */
export type EmbedSearch = z.infer<typeof embedSearchSchema>;

/**
 * shareSearchSchema is the /studio/share search schema: the shared target — an
 * itinerary or a collection — and the resource-scoped share token that authorizes
 * the read-only view. All three reuse the shared optional-string-with-catch parser,
 * so a malformed or absent param resolves to undefined and the route degrades to
 * the invalid-link screen rather than throwing. Unlike the embed schema it carries
 * NO display-config params — the share view honors the app defaults (and the org
 * default_props), so there is nothing for a visitor to tune. The token is read out
 * of the shared link here and then rides the X-Share-Token header on every read
 * (never a URL credential the backend logs).
 *
 * EXACTLY ONE target is meaningful, because a token is minted for one resource and
 * the surface has to know which one it was handed. A link naming both drops both
 * rather than picking: guessing would render one resource with a token the other
 * was minted for, and the invalid-link screen is the honest answer.
 */
export const shareSearchSchema = z
  .object({
    collection_id: optionalString,
    itinerary_id: optionalString,
    share_token: optionalString,
  })
  .transform((search) =>
    search.collection_id !== undefined && search.itinerary_id !== undefined
      ? { ...search, collection_id: undefined, itinerary_id: undefined }
      : search,
  );

/**
 * ShareSearch is the parsed /studio/share search, derived from shareSearchSchema.
 */
export type ShareSearch = z.infer<typeof shareSearchSchema>;

/**
 * embedConfigSchema is the fully-resolved embed configuration the studio
 * components consume. It is the post-precedence shape: every control flag is a
 * concrete boolean, the preference/terrain values are resolved, and the content
 * selection carries the resolved map-state ids. Zod-first so resolution output is
 * validated and the EmbedConfig type is derived from it.
 */
export const embedConfigSchema = z.object({
  activity: z.string().nullable(),
  collectionId: z.string().nullable(),
  embedded: z.literal(true),
  enrichments: z.array(z.string()),
  itineraryId: z.string().nullable(),
  overlay: z.string().nullable(),
  preferredLanguage: languageSchema,
  preferredTheme: embedThemeSchema,
  preferredUnitLength: unitLengthSchema,
  preferredUnitSlope: unitSlopeSchema,
  preferredUnitTemperature: unitTemperatureSchema,
  // The LAST line of defence before this value reaches MapLibre: a tint is written
  // verbatim into a line-color and into a marker sprite id, and an invalid color
  // breaks the itinerary layers outright. `.catch("")` degrades a malformed org
  // value to "no tint" instead, so a bad stored color costs the tint, never the map.
  segmentsColor: segmentsColorSchema.catch(""),
  terrain: z.string(),
  terrainVariant: terrainVariantSchema,
  withDirectionsControl: z.boolean(),
  withDownloadControl: z.boolean(),
  withNavigationControl: z.boolean(),
  withOverlays: z.array(z.string()),
  withOverlaysControl: z.boolean(),
  withPreferencesControl: z.boolean(),
  withProfileAnalysis: z.boolean(),
  withProfileCharacteristics: z.boolean(),
  withProfileElevation: z.boolean(),
  withStreetviewControl: z.boolean(),
  withTerrains: z.array(z.string()),
  zoneId: z.string().nullable(),
});

/**
 * EmbedConfig is the fully-resolved embed configuration, derived from
 * embedConfigSchema.
 */
export type EmbedConfig = z.infer<typeof embedConfigSchema>;

/**
 * resolveBoolean applies the control-flag precedence: an explicit URL value
 * (true/false) wins, else the org default_props value, else the hardcoded
 * fallback. undefined from the URL means "not specified" — the urlBoolean parser
 * maps an unrecognized/absent param to undefined.
 */
const resolveBoolean = (
  urlValue: boolean | undefined,
  orgDefault: boolean | undefined,
  fallback: boolean,
): boolean => {
  if (urlValue !== undefined) {
    return urlValue;
  }

  return orgDefault ?? fallback;
};

/**
 * resolveEmbedConfig folds the parsed URL search params over the org's
 * default_props into the fully-resolved EmbedConfig the studio consumes. The
 * precedence is URL value > organization default_props > hardcoded fallback. It
 * is render-free and pure (the route calls it in its loader); content ids come
 * from the shared map-state facet, control flags and preferences from the
 * embed-config facet + default_props. The result is parsed through
 * embedConfigSchema so resolution output is validated.
 *
 * `collection_id` and `itinerary_id` are both CONTENT SELECTORS, and which of them
 * is present decides what the embed renders: a collection alone renders the whole
 * collection, a collection with an itinerary renders the collection framed on that
 * itinerary, and an itinerary alone renders just that itinerary. What a collection
 * embed may expose of its contents is clamped server-side by the owning
 * organization's settings, so there is nothing for a publisher to spell per embed.
 */
export const resolveEmbedConfig = (
  search: EmbedSearch,
  orgDefaultProps: EmbeddedStudioDefaultProps,
): EmbedConfig => {
  // route_id is the temporary compatibility alias; itinerary_id wins when both are set.
  const itineraryId = search.itinerary_id ?? search.route_id ?? null;

  return embedConfigSchema.parse({
    activity: search.activity ?? null,
    collectionId: search.collection_id ?? null,
    embedded: true,
    // The active enrichment overlays (e.g. hillshade), from the repeated `enrichments`
    // param; empty when absent (no org default, like `overlay`). The programmatic `pois`
    // enrichment is never carried on the URL, so it never seeds here.
    enrichments: search.enrichments ?? [],
    itineraryId,
    overlay: search.overlay ?? null,
    preferredLanguage: search.preferred_language ?? orgDefaultProps.preferred_language,
    // The embed has no "auto" theme, so an org default of "auto" (valid for the
    // in-app studio + stored default_props) clamps to light here; an explicit URL
    // light/dark override still wins.
    preferredTheme:
      search.preferred_theme ?? (orgDefaultProps.preferred_theme === "dark" ? "dark" : "light"),
    preferredUnitLength: search.preferred_unit_length ?? orgDefaultProps.preferred_unit_length,
    preferredUnitSlope: search.preferred_unit_slope ?? orgDefaultProps.preferred_unit_slope,
    preferredUnitTemperature:
      search.preferred_unit_temperature ?? orgDefaultProps.preferred_unit_temperature,
    // The embed-only segment tint. Unlike terrain this uses ?? (not ||), because an
    // empty string is a MEANINGFUL value here rather than an unspecified one: it is
    // how a publisher turns an org-wide tint off for one embed. So an explicit
    // `segments_color=` beats the org default, and only an ABSENT param falls through
    // to it. The resolved "" means every segment keeps its own color.
    segmentsColor: search.segments_color ?? orgDefaultProps.segments_color ?? "",
    // Terrain uses || (not ??) so an empty default_props.terrain falls through to
    // the hardcoded fallback (an empty string must not count as "specified").
    terrain: search.terrain || orgDefaultProps.terrain || DEFAULT_TERRAIN,
    terrainVariant: search.terrain_variant ?? orgDefaultProps.terrain_variant ?? "light",
    withDirectionsControl: resolveBoolean(
      search.with_directions_control,
      orgDefaultProps.with_directions_control,
      false,
    ),
    withDownloadControl: resolveBoolean(
      search.with_download_control,
      orgDefaultProps.with_download_control,
      false,
    ),
    withNavigationControl: resolveBoolean(
      search.with_navigation_control,
      orgDefaultProps.with_navigation_control,
      true,
    ),
    withOverlays: search.with_overlays ?? orgDefaultProps.with_overlays ?? [],
    withOverlaysControl: resolveBoolean(
      search.with_overlays_control,
      orgDefaultProps.with_overlays_control,
      false,
    ),
    withPreferencesControl: resolveBoolean(
      search.with_preferences_control,
      orgDefaultProps.with_preferences_control,
      true,
    ),
    withProfileAnalysis: resolveBoolean(
      search.with_profile_analysis,
      orgDefaultProps.with_profile_analysis,
      true,
    ),
    withProfileCharacteristics: resolveBoolean(
      search.with_profile_characteristics,
      orgDefaultProps.with_profile_characteristics,
      true,
    ),
    withProfileElevation: resolveBoolean(
      search.with_profile_elevation,
      orgDefaultProps.with_profile_elevation,
      true,
    ),
    withStreetviewControl: resolveBoolean(
      search.with_streetview_control,
      orgDefaultProps.with_streetview_control,
      false,
    ),
    withTerrains: search.with_terrains ?? orgDefaultProps.with_terrains ?? [],
    zoneId: search.zone_id ?? null,
  });
};
